/**
 * Settings Adapter
 *
 * SOLE mapping layer between BeHeard's data sources and the shared
 * `@levelup/ui-management/settings` section components.
 *
 * SettingsPageV2 is a pure UI composer: it imports these mappers, wires
 * them with raw inputs from hooks/Redux, and passes the resulting props
 * straight to the shared shell sections.
 *
 * RULES (do not violate):
 * - Pure functions only. No React state, no side effects.
 * - No data fetching here. Callers fetch; this layer reshapes only.
 * - Central is the SOLE authority for profile + workspace edits — the
 *   `onEdit*` callbacks built here open Central with returnUrl.
 * - Bearer-only. No cookies. No proxy.
 */

import type {
  ProfileSectionProps,
  WorkspaceSectionProps,
  BillingSectionProps,
  NotificationPreferencesSectionProps,
} from '@levelup/ui-management/settings';
import type { BillingState } from '@levelup/ui-management/billing';
import type {
  CategoryPreference,
  UpdateCategoryParams,
} from '@levelup/central-notifications';
import type {
  CentralRole,
  MembershipPermissions,
  WorkspaceMember,
  PendingInvite,
} from '@levelup/central-auth';
import type { SubscriptionStatus } from '@levelup/central-billing';
import {
  suggestSelectableRoles,
  suggestAssignableRoles,
} from '@levelup/central-context';

// ============================================================
// Types
// ============================================================

/** Subset of the Central user we need for ProfileSection. */
export interface CentralUserLike {
  id: string;
  email: string;
  fullName: string | null;
  avatarUrl: string | null;
}

/** Subset of the boot workspace we need for WorkspaceSection / Members. */
export interface BootWorkspaceLike {
  id: string;
  name: string;
  slug: string;
  type?: 'personal' | 'business';
  role?: CentralRole | string;
  avatar?: { type: 'image' | 'initials'; imageUrl?: string; initials?: string } | null;
  permissions?: {
    manageMembers: boolean;
    manageBilling: boolean;
    transferOwnership: boolean;
  };
}

/** Raw billing facts pulled from BeHeard's RTK Query `getBillingStatus` + `getCredits`. */
export interface BillingFacts {
  planKey?: string;
  planName?: string;
  status?: string;
  currentPeriodEnd?: string | null;
  cancelAtPeriodEnd?: boolean;
  /** Central's stated credit balance; null = unknown (never shown as 0). */
  creditsTotal: number | null;
  isLoading: boolean;
  hasError: boolean;
  canManage: boolean;
}

/** Notification fetch state — real Central state, not a fake empty array. */
export type NotificationsFetchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  /** Central returned 200 but no preferences exist yet for this user. */
  | { status: 'not-configured' }
  | { status: 'ready'; preferences: CategoryPreference[] };

/** Raw inputs the Members tab needs from the page layer. */
export interface MembersFacts {
  workspaceId: string;
  currentRole: CentralRole;
  canManageMembers: boolean;
}

// ============================================================
// Profile mapping
// ============================================================

export interface ProfileAdapterInput {
  user: CentralUserLike | null;
  centralUrl: string;
  currentHref: string;
}

export function buildProfileSectionProps(
  input: ProfileAdapterInput,
): ProfileSectionProps {
  const { user, centralUrl, currentHref } = input;
  return {
    avatarUrl: user?.avatarUrl ?? null,
    fullName: user?.fullName ?? null,
    email: user?.email ?? '',
    onEditProfile: () => {
      if (!centralUrl) return;
      const returnUrl = encodeURIComponent(currentHref);
      window.open(`${centralUrl}/settings/profile?returnUrl=${returnUrl}`, '_blank');
    },
  };
}

// ============================================================
// Workspace mapping
// ============================================================

export interface WorkspaceAdapterInput {
  workspace: BootWorkspaceLike | null;
  canTransferOwnership: boolean;
  centralUrl: string;
  currentHref: string;
}

export function buildWorkspaceSectionProps(
  input: WorkspaceAdapterInput,
): WorkspaceSectionProps {
  const { workspace, canTransferOwnership, centralUrl, currentHref } = input;
  const returnUrl = encodeURIComponent(currentHref);
  const wsAvatarUrl =
    workspace?.avatar?.type === 'image' && workspace.avatar.imageUrl
      ? workspace.avatar.imageUrl
      : null;

  const openCentralPath = (path: string) => {
    if (!centralUrl || !workspace?.slug) return;
    window.open(`${centralUrl}${path}?returnUrl=${returnUrl}`, '_blank');
  };

  return {
    name: workspace?.name ?? '',
    slug: workspace?.slug ?? '',
    type: (workspace?.type as 'personal' | 'business') ?? 'business',
    role: workspace?.role ?? 'viewer',
    avatarUrl: wsAvatarUrl,
    onEditWorkspace: () => openCentralPath(`/workspace/${workspace?.slug}/settings`),
    onTransferOwnership: canTransferOwnership
      ? () => openCentralPath(`/workspace/${workspace?.slug}/transfer`)
      : undefined,
    onDeleteWorkspace: canTransferOwnership
      ? () => openCentralPath(`/workspace/${workspace?.slug}/close`)
      : undefined,
  };
}

// ============================================================
// Billing mapping
// ============================================================

export interface BillingAdapterInput {
  facts: BillingFacts;
  onPortal: () => void;
}

export function buildBillingSectionProps(
  input: BillingAdapterInput,
): BillingSectionProps {
  const { facts, onPortal } = input;
  // Anything Central did not state is UNAVAILABLE — never a default of
  // 0 credits, plan "Free" or status "active". The section then shows the
  // reason in place of the figures (BillingSection replaces content on error).
  const unavailable = billingUnavailableReason(facts);
  const status = (facts.status ?? 'unknown') as SubscriptionStatus;

  const billingState: BillingState = {
    plan: {
      planCode: facts.planKey ?? 'unavailable',
      planName: facts.planName ?? 'Unavailable',
      status,
      currentPeriodEnd: facts.currentPeriodEnd ?? null,
      cancelAtPeriodEnd: facts.cancelAtPeriodEnd ?? false,
      trialEndsAt: null,
    },
    credits: {
      // Never rendered when unknown: `error` replaces the content.
      balance: facts.creditsTotal ?? Number.NaN,
    },
    billing: {
      available: facts.canManage,
      provider: 'stripe' as const,
    },
    upgradeAvailable: !unavailable && facts.canManage && status !== 'canceled',
  };

  return {
    billingState,
    onManageBilling: onPortal,
    onAddCredits: onPortal,
    onUpgrade: onPortal,
    isLoading: facts.isLoading,
    error: facts.isLoading ? null : unavailable,
  };
}

/**
 * Why the billing figures cannot be shown, or null when Central stated them
 * all. Unknown is said as such — it is never filled in with 0 / Free / active.
 */
export function billingUnavailableReason(facts: BillingFacts): string | null {
  if (facts.hasError) return 'Billing details are unavailable right now: Central could not be read. Try again shortly.';
  if (facts.creditsTotal === null) return 'Your credit balance is unavailable right now: Central did not report it.';
  if (!facts.planName || !facts.status) return 'Your plan is unavailable: Central reported no subscription on file.';
  return null;
}

// ============================================================
// Members mapping
// ============================================================

export interface MembersPermissionsAdapterInput {
  currentRole: CentralRole;
  canManageMembers: boolean;
}

/**
 * Build the canonical MembershipPermissions object the shared
 * MembershipList / InviteMemberModal / PendingInvitesList expect.
 */
export function buildMembershipPermissions(
  input: MembersPermissionsAdapterInput,
): MembershipPermissions {
  const { currentRole, canManageMembers } = input;
  return {
    canInvite: canManageMembers,
    canRemoveMembers: canManageMembers,
    canChangeRoles: canManageMembers,
    canCancelInvites: canManageMembers,
    selectableRoles: suggestSelectableRoles(currentRole),
    assignableRoles: suggestAssignableRoles(currentRole),
  };
}

/** Pass-through bag of props the MembersTab UI consumes. */
export interface MembersViewModel {
  permissions: MembershipPermissions;
  members: WorkspaceMember[];
  invites: PendingInvite[];
  isLoadingMembers: boolean;
  isLoadingInvites: boolean;
  membersError: string | null;
  invitesError: string | null;
}

// ============================================================
// Notifications mapping
// ============================================================

export interface NotificationsAdapterInput {
  state: NotificationsFetchState;
  onUpdateCategory: (params: UpdateCategoryParams) => void;
}

/**
 * Map the real Central fetch state into NotificationPreferencesSection props.
 *
 * - `loading`         → `isLoading: true`
 * - `error`           → `error: <message>`           (real failure)
 * - `not-configured`  → `preferences: []`, `error: null`, `isLoading: false`
 *                       (valid empty state — the page renders a neutral
 *                       informational banner above the section explaining
 *                       that no categories have been configured yet)
 * - `ready`           → live preferences
 *
 * The page is responsible for rendering the "no categories yet" notice
 * because it is a UX hint, not an error — the shared section MUST NOT
 * be told to show error styling for a valid empty server response.
 */
export function buildNotificationsSectionProps(
  input: NotificationsAdapterInput,
): NotificationPreferencesSectionProps {
  const { state, onUpdateCategory } = input;

  const handleUpdate: NotificationPreferencesSectionProps['onUpdateCategory'] = (
    category,
    channel,
    enabled,
  ) => {
    onUpdateCategory({
      category,
      channels: { [channel]: enabled } as Partial<
        Record<'inbox' | 'push' | 'email', boolean>
      >,
    });
  };

  switch (state.status) {
    case 'idle':
    case 'loading':
      return {
        preferences: [],
        onUpdateCategory: handleUpdate,
        isLoading: true,
        error: null,
      };
    case 'error':
      return {
        preferences: [],
        onUpdateCategory: handleUpdate,
        isLoading: false,
        error: state.message,
      };
    case 'not-configured':
      return {
        preferences: [],
        onUpdateCategory: handleUpdate,
        isLoading: false,
        error: null,
      };
    case 'ready':
      return {
        preferences: state.preferences,
        onUpdateCategory: handleUpdate,
        isLoading: false,
        error: null,
      };
  }
}

/**
 * Helper for the page layer: should we render a neutral info banner above
 * the preferences section? True only when Central confirmed there are no
 * categories yet (NOT during loading and NOT on real errors).
 */
export function isNotificationsNotConfigured(state: NotificationsFetchState): boolean {
  return state.status === 'not-configured';
}
